const cacheKey = (projectId) => `docValidation:${projectId}`;

/** MongoDB treats `.` in object keys as path separators — encode before persist. */
const DOT_TOKEN = '__DOT__';

export const encodeValidationFileKey = (key) =>
  String(key || '').replace(/\./g, DOT_TOKEN);

export const decodeValidationFileKey = (key) =>
  String(key || '').replace(new RegExp(DOT_TOKEN, 'g'), '.');

const looksLikeStatusObject = (value) =>
  value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  ('isValid' in value || 'reason' in value || 'expectedType' in value || 'contentValid' in value);

/**
 * Flatten fileStatus that Mongo/Mongoose nested because keys contained `.pdf` etc.
 * Also decode __DOT__ tokens back to `.`.
 */
export const flattenFileStatusMap = (raw, prefix = '') => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};

  Object.entries(raw).forEach(([key, value]) => {
    const decodedKey = decodeValidationFileKey(key);
    const path = prefix ? `${prefix}.${decodedKey}` : decodedKey;

    if (looksLikeStatusObject(value)) {
      out[path] = value;
      // Also keep encoded form for direct lookups during transition
      out[encodeValidationFileKey(path)] = value;
      return;
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(out, flattenFileStatusMap(value, path));
      return;
    }
  });

  return out;
};

/** Encode map keys so MongoDB will not nest on `.` in filenames. */
export const sanitizeFileStatusForPersist = (validationMap = {}) => {
  const flat = flattenFileStatusMap(validationMap);
  const out = {};
  Object.entries(flat).forEach(([key, value]) => {
    if (!looksLikeStatusObject(value)) return;
    // Prefer decoded canonical key encoded once (avoid double-encoding)
    const canonical = decodeValidationFileKey(key);
    out[encodeValidationFileKey(canonical)] = value;
  });
  return out;
};

/** Build payload stored on clinical intake draft and in session cache. */
export const buildPersistedValidationState = (
  fingerprint,
  allValid,
  validationMap,
  validationCompleted = true
) => ({
  fingerprint,
  allValid: Boolean(allValid),
  validationCompleted: Boolean(validationCompleted),
  validatedAt: new Date().toISOString(),
  fileStatus: sanitizeFileStatusForPersist(validationMap || {}),
});

/** Normalize persisted validation (server or session) into a common shape. */
export const normalizePersistedValidation = (raw) => {
  if (!raw?.fingerprint) return null;
  const validationMap = flattenFileStatusMap(raw.validationMap || raw.fileStatus || {});
  return {
    fingerprint: raw.fingerprint,
    allValid: Boolean(raw.allValid),
    validationCompleted: raw.validationCompleted !== false,
    validationMap,
    validatedAt: raw.validatedAt || null,
  };
};

/**
 * Resolve a file's validation status from a map, tolerating nested Mongo keys,
 * encoded dots, and re-upload blob-name prefixes.
 */
export const resolveValidationStatusFromMap = (fileStatusMap, file, documentType) => {
  if (!file || !documentType) return undefined;
  const map = flattenFileStatusMap(fileStatusMap || {});
  const bases = [file.azureBlobName, file.filename, file.originalname, file.name]
    .filter(Boolean)
    .map(String);
  const suffix = `_${documentType}`;

  const candidates = [];
  bases.forEach((base) => {
    candidates.push(`${base}${suffix}`);
    candidates.push(encodeValidationFileKey(`${base}${suffix}`));
  });

  for (const key of candidates) {
    if (map[key]) return map[key];
  }

  for (const [key, status] of Object.entries(map)) {
    if (!looksLikeStatusObject(status)) continue;
    const decodedKey = decodeValidationFileKey(key);
    if (!decodedKey.endsWith(suffix) && !key.endsWith(encodeValidationFileKey(suffix))) continue;
    const keyBase = decodedKey.endsWith(suffix)
      ? decodedKey.slice(0, -suffix.length)
      : decodeValidationFileKey(key.slice(0, -encodeValidationFileKey(suffix).length));

    if (
      bases.some(
        (b) =>
          b === keyBase ||
          keyBase.endsWith(b) ||
          b.endsWith(keyBase) ||
          keyBase.includes(b) ||
          b.includes(keyBase)
      )
    ) {
      return status;
    }
  }

  // Last resort: match by leaf filename (e.g. "Full Protocol-1-9.pdf") within this doc type
  const leafNames = bases
    .map((b) => b.split(/[/\\]/).pop())
    .filter((n) => n && n.includes('.'));
  for (const [key, status] of Object.entries(map)) {
    if (!looksLikeStatusObject(status)) continue;
    const decodedKey = decodeValidationFileKey(key);
    if (!decodedKey.endsWith(suffix)) continue;
    if (leafNames.some((leaf) => decodedKey.includes(leaf))) {
      return status;
    }
  }

  return undefined;
};

/** Load validation from clinical intake document_uploads when fingerprint matches. */
export const loadPersistedValidationState = (documentUploads, fingerprint) => {
  if (!documentUploads?.validation_state || !fingerprint) return null;
  const vs = documentUploads.validation_state;
  if (vs.fingerprint !== fingerprint) return null;
  if (!vs.validationCompleted) return null;
  return normalizePersistedValidation({
    fingerprint: vs.fingerprint,
    allValid: vs.allValid,
    validationCompleted: vs.validationCompleted,
    fileStatus: vs.fileStatus,
    validatedAt: vs.validatedAt,
  });
};

/** Load cached validation results for display (pass or fail). */
export const loadDocumentValidationCache = (projectId, fingerprint) => {
  if (!projectId || !fingerprint) return null;
  try {
    const raw = sessionStorage.getItem(cacheKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.fingerprint !== fingerprint) return null;
    return normalizePersistedValidation(parsed);
  } catch {
    return null;
  }
};

/** Prefer server-persisted validation, then session cache. */
export const loadDocumentValidationState = (projectId, documentUploads, fingerprint) => {
  const fromServer = loadPersistedValidationState(documentUploads, fingerprint);
  if (fromServer) return { ...fromServer, source: 'server' };
  const fromSession = loadDocumentValidationCache(projectId, fingerprint);
  if (fromSession) return { ...fromSession, source: 'session' };
  return null;
};

/** True only when cached validation passed for the current file fingerprint. */
export const isDocumentValidationCachePassing = (cached) => Boolean(cached?.allValid);

export const saveDocumentValidationCache = (projectId, payload) => {
  if (!projectId || !payload?.fingerprint) return;
  try {
    const validationMap = flattenFileStatusMap(payload.validationMap || payload.fileStatus || {});
    sessionStorage.setItem(
      cacheKey(projectId),
      JSON.stringify({
        ...payload,
        validationMap,
        fileStatus: sanitizeFileStatusForPersist(validationMap),
        validatedAt: payload.validatedAt || new Date().toISOString(),
      })
    );
  } catch {
    // ignore quota errors
  }
};

export const clearDocumentValidationCache = (projectId) => {
  if (!projectId) return;
  try {
    sessionStorage.removeItem(cacheKey(projectId));
  } catch {
    // ignore
  }
};
